import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, FlaskConical, MapPin, Workflow, Warehouse, Users, Check, Circle, Hourglass, Factory, Truck, ClipboardList, Contact } from "lucide-react";
import { PageHeader, StatCard, GlassCard, TIP_ETIKET } from "@/components/erp";
import { useAuth } from "@/context/AuthContext";
import { PanelGrafik } from "@/components/PanelGrafik";
import { can } from "@/lib/perms";
import api from "@/lib/api";

const YOL = [
  { ad: "Temel: giriş, ayarlar, lokasyon/depo, ürün, reçete & maliyet", durum: "bitti" },
  { ad: "Cari & müşteri bazlı maliyet+kâr fiyat listeleri", durum: "bitti" },
  { ad: "Sipariş: maliyet tabanı + risk limiti + termin + onay akışı", durum: "bitti" },
  { ad: "Depo takip: stok hareketleri, rezervasyon, lot/SKT, transfer", durum: "bitti" },
  { ad: "Üretim: istasyon bazlı iş emri ve kişiden kişiye devir", durum: "bitti" },
  { ad: "Tahsilat & cari ekstre (nakit, havale, POS, çek, senet)", durum: "bitti" },
  { ad: "Sevkiyat & kargolama, irsaliye, palet/RFID", durum: "sirada" },
  { ad: "Satın alma, finans, muhasebe, e-fatura", durum: "" },
  { ad: "İK / PDKS, saha satış, B2B portal", durum: "" },
];

export default function Dashboard() {
  const { user, ayar } = useAuth();
  const [o, setO] = useState(null);
  useEffect(() => {
    api.get("/dashboard/ozet").then((r) => setO(r.data));
  }, []);

  const sd = o?.siparis_durumlari || {};
  const akis = [
    { label: "Onay bekleyen", value: sd.onay_bekliyor || 0, icon: Hourglass, to: "/siparisler" },
    { label: "Üretimde", value: sd.uretimde || 0, icon: Factory, to: "/siparisler" },
    { label: "Sevke hazır", value: sd.sevke_hazir || 0, icon: Truck, to: "/siparisler" },
    { label: "Aktif iş emri", value: o?.aktif_is_emri || 0, icon: ClipboardList, to: "/is-emirleri" },
    { label: "Cari", value: o?.cari_sayisi || 0, icon: Contact, to: "/cariler" },
    { label: "Sevk edilen", value: sd.sevk_edildi || 0, icon: Check, to: "/siparisler" },
  ];
  const stats = [
    { label: "Ürün", value: o?.urun_sayisi, icon: Package, to: "/urunler" },
    { label: "Reçete", value: o?.recete_sayisi, icon: FlaskConical, to: "/receteler" },
    { label: "Lokasyon", value: o?.lokasyon_sayisi, icon: MapPin, to: "/lokasyonlar" },
    { label: "Depo", value: o?.depo_sayisi, icon: Warehouse, to: "/lokasyonlar" },
    { label: "Aktif İstasyon", value: o?.istasyon_sayisi, icon: Workflow, to: "/istasyonlar" },
    { label: "Kullanıcı", value: o?.kullanici_sayisi, icon: Users, to: "/cariler?kategori=personel" },
  ];

  return (
    <div data-testid="dashboard-page">
      <PageHeader eyebrow={ayar?.sektor_adi} title={`Hoş geldiniz, ${user?.name || ""}`} desc={`${ayar?.unvan || ""} · genel durum özeti`} />
      {can(user, "odeme:read") && <PanelGrafik />}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 mb-4">
        {akis.map((s, i) => (
          <Link key={s.label} to={s.to}><StatCard label={s.label} value={o ? s.value : "—"} icon={s.icon} delay={i * 60} testid={`akis-${s.label}`} /></Link>
        ))}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        {stats.map((s, i) => (
          <Link key={s.label} to={s.to}><StatCard label={s.label} value={o ? s.value : "—"} icon={s.icon} delay={i * 60} testid={`stat-${s.label}`} /></Link>
        ))}
      </div>
      <div className="grid lg:grid-cols-[1fr_1.4fr] gap-6 mt-8">
        <GlassCard className="p-6 fade-up">
          <h3 className="font-head font-semibold">Ürün dağılımı</h3>
          <div className="space-y-4 mt-6">
            {Object.entries(o?.tip_dagilimi || {}).map(([k, v]) => {
              const pct = o?.urun_sayisi ? (v / o.urun_sayisi) * 100 : 0;
              return (
                <div key={k} data-testid={`dagilim-${k}`}>
                  <div className="flex justify-between text-sm"><span>{TIP_ETIKET[k]}</span><span className="font-mono">{v}</span></div>
                  <div className="h-2 rounded-full bg-white/5 mt-1.5 overflow-hidden"><div className="h-full grad-bg rounded-full transition-[width] duration-700" style={{ width: `${pct}%` }} /></div>
                </div>
              );
            })}
          </div>
        </GlassCard>
        <GlassCard className="p-6 fade-up" style={{ animationDelay: "120ms" }}>
          <h3 className="font-head font-semibold">Yol haritası</h3>
          <p className="text-xs text-muted-foreground mt-1">Sipariş → maliyet → termin → üretim → depo → kargo zinciri adım adım kuruluyor.</p>
          <ol className="mt-5 space-y-3">
            {YOL.map((y, i) => (
              <li key={y.ad} className="flex items-center gap-3 text-sm" data-testid={`yol-${i}`}>
                {y.durum === "bitti" ? <span className="w-6 h-6 rounded-full grad-bg flex items-center justify-center"><Check className="w-3.5 h-3.5 text-white" /></span>
                  : <Circle className={`w-6 h-6 ${y.durum === "sirada" ? "text-[var(--g1)]" : "text-white/15"}`} />}
                <span className={y.durum ? "" : "text-muted-foreground"}>{y.ad}</span>
                {y.durum === "sirada" && <span className="ml-auto text-[10px] uppercase tracking-wider text-[var(--g1)]">sırada</span>}
              </li>
            ))}
          </ol>
        </GlassCard>
      </div>
    </div>
  );
}
